import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { TrainingTopic, PlanCategory, TrainingQuizResponse } from '../../types/trainingTypes';
import { TOPIC_STYLE_COLORS } from '../../types/trainingTypes';
import { getUrl } from 'aws-amplify/storage';
import { uploadData } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import { authClient } from '../../utils/amplifyClient';
import { S3_IMAGE_PATH } from '../../utils/constants';
import { createTrainingQuizResponse } from '../../graphql/trainingQueries';
import { BackButton } from '../ui';
import '../../css/TopicContentViewerModal.css';

interface QuizPrompt {
    type: string;
    component: string;
    description: string;
    options: string[];
    correctAnswer?: string;
}

interface TopicContentViewerModalProps {
    isOpen: boolean;
    onClose: () => void;
    topic: TrainingTopic | null;
    /** Called with the S3 signaturePath after user signs and confirms */
    onMarkAsViewed?: (signaturePath: string) => void;
    companyId?: string;
    /** Quiz props — when provided, quiz appears after content and before signature */
    quizCategory?: PlanCategory | null;
    quizPlanId?: string;
    quizUserId?: string;
    quizCycleStartDate?: string;
    quizExistingAttempts?: TrainingQuizResponse[];
    /** Called after quiz response is saved (so parent can update state) */
    onQuizSubmitted?: (passed: boolean) => void;
}

type ViewerStep = 'viewing' | 'quiz' | 'signing' | 'submitting' | 'done';

/** Minimum seconds a user must spend viewing non-video content before signing */
const MIN_VIEW_SECONDS = 30;

/**
 * Content viewer modal — video, PDF, or image viewer with metadata.
 * Flow: View Content → Quiz (if due) → Single Signature → Done
 *
 * Content completion is verified before proceeding:
 *   - Video: must watch to the end (onEnded event)
 *   - PDF/Image/Poster: minimum viewing timer
 *
 * If a quiz is due for the topic's category, the quiz questions appear
 * after content is viewed. The user then signs ONCE — that single signature
 * covers both the training completion and the quiz submission.
 */
const TopicContentViewerModal = ({
    isOpen,
    onClose,
    topic,
    onMarkAsViewed,
    companyId = '',
    quizCategory,
    quizPlanId,
    quizUserId,
    quizCycleStartDate,
    quizExistingAttempts = [],
    onQuizSubmitted,
}: TopicContentViewerModalProps) => {
    const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
    const [step, setStep] = useState<ViewerStep>('viewing');
    const [hasSignature, setHasSignature] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Content completion verification state
    const [contentCompleted, setContentCompleted] = useState(false);
    const [viewSeconds, setViewSeconds] = useState(0);
    const [showNotReadyAlert, setShowNotReadyAlert] = useState(false);
    const viewTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    // Signature canvas
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const lastPointRef = useRef<{ x: number; y: number } | null>(null);

    // Quiz state
    const [quizQuestions, setQuizQuestions] = useState<QuizPrompt[]>([]);
    const [quizLoading, setQuizLoading] = useState(false);
    const [quizCurrentIndex, setQuizCurrentIndex] = useState(0);
    const [quizAnswers, setQuizAnswers] = useState<{ [idx: number]: string }>({});
    const [quizShowReview, setQuizShowReview] = useState(false);

    // Whether quiz is applicable for this session
    const hasQuiz = !!(quizCategory?.requiresQuiz && quizCategory?.quizFormId);

    const contentType = useMemo(() => {
        if (!topic) return 'none';
        const style = topic.style;
        if (style === 'Video') return 'video';
        if (style === 'PDF') return 'pdf';
        if (style === 'Poster') return 'image';
        const path = (topic.contentFilePath ?? topic.contentUrl ?? '').toLowerCase();
        if (path.endsWith('.mp4') || path.endsWith('.mov')) return 'video';
        if (path.endsWith('.pdf')) return 'pdf';
        if (path.endsWith('.jpg') || path.endsWith('.jpeg') || path.endsWith('.png')) return 'image';
        return 'other';
    }, [topic]);

    // Required viewing seconds for non-video content
    const requiredViewSeconds = useMemo(() => {
        if (contentType === 'video') return 0;
        return MIN_VIEW_SECONDS;
    }, [contentType]);

    // Resolve S3 URL & reset state when modal opens/closes
    useEffect(() => {
        if (!isOpen || !topic) {
            setResolvedUrl(null);
            setStep('viewing');
            setHasSignature(false);
            setError(null);
            setContentCompleted(false);
            setViewSeconds(0);
            setShowNotReadyAlert(false);
            setQuizQuestions([]);
            setQuizCurrentIndex(0);
            setQuizAnswers({});
            setQuizShowReview(false);
            if (viewTimerRef.current) {
                clearInterval(viewTimerRef.current);
                viewTimerRef.current = null;
            }
            return;
        }
        if (topic.contentUrl) { setResolvedUrl(topic.contentUrl); return; }
        if (topic.contentFilePath) {
            (async () => {
                try {
                    const result = await getUrl({ path: topic.contentFilePath! });
                    setResolvedUrl(result.url.toString());
                } catch (err) {
                    console.error('Failed to resolve S3 URL:', err);
                    setResolvedUrl(null);
                }
            })();
            return;
        }
        setResolvedUrl(null);
    }, [isOpen, topic]);

    // Start viewing timer for non-video content once URL is resolved
    useEffect(() => {
        if (!isOpen || !resolvedUrl || contentType === 'video' || step !== 'viewing') return;
        if (contentCompleted) return;

        viewTimerRef.current = setInterval(() => {
            setViewSeconds(prev => {
                const next = prev + 1;
                if (next >= requiredViewSeconds) {
                    setContentCompleted(true);
                    if (viewTimerRef.current) {
                        clearInterval(viewTimerRef.current);
                        viewTimerRef.current = null;
                    }
                }
                return next;
            });
        }, 1000);

        return () => {
            if (viewTimerRef.current) {
                clearInterval(viewTimerRef.current);
                viewTimerRef.current = null;
            }
        };
    }, [isOpen, resolvedUrl, contentType, step, contentCompleted, requiredViewSeconds]);

    // Video ended handler
    const handleVideoEnded = useCallback(() => {
        setContentCompleted(true);
    }, []);

    const styleColor = useMemo(
        () => (topic ? TOPIC_STYLE_COLORS[topic.style] ?? '#6b7280' : '#6b7280'),
        [topic],
    );

    const displayUrl = useMemo(() => {
        if (!resolvedUrl) return null;
        if (contentType === 'pdf') {
            return `https://docs.google.com/viewer?url=${encodeURIComponent(resolvedUrl)}&embedded=true`;
        }
        return resolvedUrl;
    }, [resolvedUrl, contentType]);

    // ---- Quiz loading ----
    const loadQuizForm = useCallback(async () => {
        if (!quizCategory?.quizFormId) return;
        setQuizLoading(true);
        try {
            const result = await authClient.graphql({
                query: /* GraphQL */ `query GetForm($id: ID!) { getForm(id: $id) { id name formData } }`,
                variables: { id: quizCategory.quizFormId },
            });
            const form = (result as any).data.getForm;
            if (!form?.formData) { setQuizLoading(false); return; }

            const parsed = JSON.parse(form.formData);
            const prompts: QuizPrompt[] = [];
            parsed.forEach((section: any) => {
                if (section.type === 'section' && section.prompts) {
                    section.prompts.forEach((p: any) => {
                        prompts.push({
                            type: p.type || 'yesNo',
                            component: p.component || '',
                            description: p.description || '',
                            options: p.options || [],
                            correctAnswer: p.correctAnswer || '',
                        });
                    });
                }
            });
            setQuizQuestions(prompts);
        } catch (err) {
            console.error('Error loading quiz form:', err);
        } finally {
            setQuizLoading(false);
        }
    }, [quizCategory]);

    // Attempt to proceed after content is viewed
    const handleReviewClick = useCallback(() => {
        if (!contentCompleted) {
            setShowNotReadyAlert(true);
            setTimeout(() => setShowNotReadyAlert(false), 4000);
            return;
        }
        // If quiz is due, go to quiz step; otherwise go straight to signing
        if (hasQuiz) {
            loadQuizForm();
            setStep('quiz');
        } else {
            setStep('signing');
        }
    }, [contentCompleted, hasQuiz, loadQuizForm]);

    // Remaining seconds display
    const remainingSeconds = Math.max(0, requiredViewSeconds - viewSeconds);

    // Button label for the review button
    const reviewButtonLabel = useMemo(() => {
        if (contentType === 'video') {
            return contentCompleted
                ? (hasQuiz ? "I've Reviewed — Continue to Quiz" : "I've Reviewed — Sign to Confirm")
                : 'Watch the full video to continue';
        }
        if (contentCompleted) return hasQuiz ? "I've Reviewed — Continue to Quiz" : "I've Reviewed — Sign to Confirm";
        return `Please review content (${remainingSeconds}s)`;
    }, [contentType, contentCompleted, remainingSeconds, hasQuiz]);

    // ---- Quiz answer handlers ----
    const handleQuizAnswer = useCallback((value: string) => {
        setQuizAnswers(prev => ({ ...prev, [quizCurrentIndex]: value }));
    }, [quizCurrentIndex]);

    const quizGoNext = useCallback(() => {
        if (quizCurrentIndex < quizQuestions.length - 1) {
            setQuizCurrentIndex(quizCurrentIndex + 1);
        } else {
            setQuizShowReview(true);
        }
    }, [quizCurrentIndex, quizQuestions.length]);

    const quizGoBack = useCallback(() => {
        if (quizShowReview) {
            setQuizShowReview(false);
        } else if (quizCurrentIndex > 0) {
            setQuizCurrentIndex(quizCurrentIndex - 1);
        }
    }, [quizShowReview, quizCurrentIndex]);

    const gradeQuiz = useCallback(() => {
        return quizQuestions.map((q, idx) => {
            const userAnswer = quizAnswers[idx] || '';
            const correctAnswer = q.correctAnswer || '';

            let isCorrect: boolean;
            if (q.type === 'checkbox') {
                // For checkbox: compare as sets (order-independent, case-insensitive)
                const userSet = new Set(userAnswer.split(',').map(s => s.trim().toLowerCase()).filter(Boolean));
                const correctSet = new Set(correctAnswer.split(',').map(s => s.trim().toLowerCase()).filter(Boolean));
                isCorrect = userSet.size === correctSet.size && [...userSet].every(v => correctSet.has(v));
            } else {
                // For yes/no and multiple choice: case-insensitive trim comparison
                isCorrect = userAnswer.trim().toLowerCase() === correctAnswer.trim().toLowerCase();
            }

            return { prompt: q.component, userAnswer, correctAnswer, isCorrect };
        });
    }, [quizQuestions, quizAnswers]);

    // Move from quiz review to signing
    const handleQuizContinueToSign = useCallback(() => {
        setStep('signing');
    }, []);

    // ---- Signature pad ----
    const getCanvasCoords = useCallback(
        (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } | null => {
            const canvas = canvasRef.current;
            if (!canvas) return null;
            const rect = canvas.getBoundingClientRect();
            if ('touches' in e) {
                const touch = e.touches[0] ?? e.changedTouches[0];
                if (!touch) return null;
                return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
            }
            return { x: e.clientX - rect.left, y: e.clientY - rect.top };
        }, [],
    );

    const startDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        const coords = getCanvasCoords(e);
        if (!coords) return;
        setIsDrawing(true);
        lastPointRef.current = coords;
    }, [getCanvasCoords]);

    const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
        if (!isDrawing) return;
        e.preventDefault();
        const coords = getCanvasCoords(e);
        if (!coords) return;
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx && lastPointRef.current) {
            ctx.strokeStyle = '#1e293b';
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.beginPath();
            ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
            ctx.lineTo(coords.x, coords.y);
            ctx.stroke();
            lastPointRef.current = coords;
            if (!hasSignature) setHasSignature(true);
        }
    }, [isDrawing, getCanvasCoords, hasSignature]);

    const stopDrawing = useCallback(() => {
        setIsDrawing(false);
        lastPointRef.current = null;
    }, []);

    const clearCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasSignature(false);
    }, []);

    // Resize canvas when signing step is shown
    useEffect(() => {
        if (step !== 'signing' || !canvasRef.current) return;
        const canvas = canvasRef.current;
        const timer = setTimeout(() => {
            const rect = canvas.getBoundingClientRect();
            canvas.width = rect.width;
            canvas.height = rect.height;
        }, 50);
        return () => clearTimeout(timer);
    }, [step]);

    // ---- Submit with signature (handles both completion + quiz) ----
    const handleSubmitWithSignature = useCallback(async () => {
        if (!hasSignature || !topic || !onMarkAsViewed) return;
        setStep('submitting');
        setError(null);
        try {
            const canvas = canvasRef.current;
            if (!canvas) throw new Error('Canvas not available');
            const blob = await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob(b => b ? resolve(b) : reject(new Error('Failed to export signature')), 'image/png');
            });

            // Upload signature once — used for both completion and quiz
            const fileName = `${uuidv4()}-TRAINING-DAILY-SIGNATURE.png`;
            const s3Path = S3_IMAGE_PATH.trainingDailySignaturePath(companyId, fileName);
            await uploadData({ path: s3Path, data: blob, options: { contentType: 'image/png' } }).result;

            // Record training completion with signature
            onMarkAsViewed(s3Path);

            // If quiz was taken, also save the quiz response with the same signature
            if (hasQuiz && quizQuestions.length > 0 && quizUserId && quizPlanId && quizCycleStartDate && quizCategory) {
                const graded = gradeQuiz();
                const score = graded.filter(a => a.isCorrect).length;
                const totalQuestions = graded.length;
                const passed = score === totalQuestions;
                const attemptNumber = (quizExistingAttempts?.length ?? 0) + 1;

                await createTrainingQuizResponse({
                    companyId,
                    userId: quizUserId,
                    planId: quizPlanId,
                    categoryId: quizCategory.id,
                    formId: quizCategory.quizFormId!,
                    responseData: JSON.stringify(graded),
                    signaturePath: s3Path,
                    passed,
                    score,
                    totalQuestions,
                    attemptNumber,
                    completedAt: new Date().toISOString(),
                    cycleStartDate: quizCycleStartDate,
                });

                onQuizSubmitted?.(passed);
            }

            setStep('done');
        } catch (err: any) {
            console.error('Failed to submit:', err);
            setError(err?.message || 'Failed to submit. Please try again.');
            setStep('signing');
        }
    }, [hasSignature, topic, onMarkAsViewed, companyId, hasQuiz, quizQuestions,
        quizUserId, quizPlanId, quizCycleStartDate, quizCategory,
        quizExistingAttempts, gradeQuiz, onQuizSubmitted]);

    if (!isOpen || !topic) return null;

    return (
        <div className="tcv-overlay" onClick={onClose}>
            <div className="tcv-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="tcv-header">
                    <h2 className="tcv-header-title">{topic.description}</h2>
                    <button className="tcv-close-btn" onClick={onClose} aria-label="Close">×</button>
                </div>

                {/* Not-ready alert */}
                {showNotReadyAlert && (
                    <div style={{
                        background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 8,
                        padding: '10px 16px', margin: '0 16px', fontSize: 13, color: '#92400e',
                        display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                        <span style={{ fontSize: 18 }}>⚠️</span>
                        <span>
                            {contentType === 'video'
                                ? 'Please watch the entire video before continuing.'
                                : `Please review the content for at least ${requiredViewSeconds} seconds before continuing.`
                            }
                        </span>
                    </div>
                )}

                {/* Content viewer */}
                {step === 'viewing' && (
                    <div className="tcv-content">
                        {!resolvedUrl ? (
                            <div className="tcv-no-content">Loading content...</div>
                        ) : contentType === 'video' ? (
                            <div className="tcv-video-container">
                                <video
                                    ref={videoRef}
                                    controls
                                    preload="metadata"
                                    src={resolvedUrl}
                                    onEnded={handleVideoEnded}
                                    style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain' }}
                                >
                                    Your browser does not support the video tag.
                                </video>
                            </div>
                        ) : contentType === 'pdf' ? (
                            <>
                                <div className="tcv-pdf-container">
                                    <iframe src={displayUrl!} title={topic.description} style={{ border: 'none' }} />
                                </div>
                                <div style={{ marginTop: 8, textAlign: 'center', fontSize: 12, color: '#64748b' }}>
                                    <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>
                                        Open document in new tab if it doesn&#39;t load
                                    </a>
                                </div>
                            </>
                        ) : contentType === 'image' ? (
                            <div className="tcv-image-container">
                                <img src={resolvedUrl} alt={topic.description} />
                            </div>
                        ) : (
                            <div className="tcv-no-content">
                                {resolvedUrl ? 'Content format not supported for inline viewing.' : 'No content available.'}
                            </div>
                        )}
                    </div>
                )}

                {/* Quiz step */}
                {step === 'quiz' && (
                    <div style={{ padding: '20px', overflowY: 'auto', maxHeight: '60vh' }}>
                        {quizLoading ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>Loading quiz...</div>
                        ) : quizQuestions.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                                No quiz questions found. You can proceed to sign.
                            </div>
                        ) : !quizShowReview ? (
                            /* Question view */
                            <div>
                                <div style={{ marginBottom: 16 }}>
                                    <h3 style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', margin: '0 0 4px' }}>
                                        📝 Completion Quiz: {quizCategory?.name}
                                    </h3>
                                    <div style={{ background: '#f1f5f9', borderRadius: 8, height: 6, overflow: 'hidden' }}>
                                        <div style={{
                                            background: '#3b82f6', height: '100%', borderRadius: 8,
                                            width: `${((quizCurrentIndex + 1) / quizQuestions.length) * 100}%`,
                                            transition: 'width 0.3s',
                                        }} />
                                    </div>
                                    <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>
                                        Question {quizCurrentIndex + 1} of {quizQuestions.length}
                                    </p>
                                </div>

                                <div style={{
                                    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                                    padding: 16, marginBottom: 16,
                                }}>
                                    <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: '0 0 6px' }}>
                                        {quizQuestions[quizCurrentIndex].component}
                                    </h4>
                                    {quizQuestions[quizCurrentIndex].description && (
                                        <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px' }}>
                                            {quizQuestions[quizCurrentIndex].description}
                                        </p>
                                    )}

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {quizQuestions[quizCurrentIndex].type === 'yesNo' && (
                                            <>
                                                {['Yes', 'No'].map(opt => (
                                                    <button key={opt} onClick={() => handleQuizAnswer(opt)} style={{
                                                        padding: '10px 16px', borderRadius: 8, fontSize: 14, fontWeight: 500,
                                                        border: quizAnswers[quizCurrentIndex] === opt ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                                                        background: quizAnswers[quizCurrentIndex] === opt ? '#eff6ff' : '#fff',
                                                        color: '#1e293b', cursor: 'pointer', textAlign: 'left',
                                                    }}>
                                                        {opt}
                                                    </button>
                                                ))}
                                            </>
                                        )}

                                        {quizQuestions[quizCurrentIndex].type === 'dropdown' && (
                                            <select
                                                value={quizAnswers[quizCurrentIndex] || ''}
                                                onChange={e => handleQuizAnswer(e.target.value)}
                                                style={{
                                                    padding: '10px 12px', borderRadius: 8, fontSize: 14,
                                                    border: '2px solid #e2e8f0', background: '#fff', color: '#1e293b',
                                                    width: '100%',
                                                }}
                                            >
                                                <option value="">Select an answer</option>
                                                {quizQuestions[quizCurrentIndex].options.map((o, i) => (
                                                    <option key={i} value={o}>{o}</option>
                                                ))}
                                            </select>
                                        )}

                                        {quizQuestions[quizCurrentIndex].type === 'checkbox' && (
                                            <>
                                                {quizQuestions[quizCurrentIndex].options.map((option, idx) => {
                                                    const currentAnswerArr = (quizAnswers[quizCurrentIndex] || '').split(',').filter(Boolean);
                                                    const isChecked = currentAnswerArr.includes(option);
                                                    return (
                                                        <label key={idx} style={{
                                                            display: 'flex', alignItems: 'center', gap: 8,
                                                            padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                                                            border: isChecked ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                                                            background: isChecked ? '#eff6ff' : '#fff',
                                                        }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={e => {
                                                                    const updated = e.target.checked
                                                                        ? [...currentAnswerArr, option]
                                                                        : currentAnswerArr.filter(v => v !== option);
                                                                    handleQuizAnswer(updated.join(','));
                                                                }}
                                                            />
                                                            <span style={{ fontSize: 14, color: '#1e293b' }}>{option}</span>
                                                        </label>
                                                    );
                                                })}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                        ) : (
                            /* Quiz review (after all questions answered) */
                            <div>
                                <h3 style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', margin: '0 0 12px' }}>
                                    Quiz Review: {quizCategory?.name}
                                </h3>
                                {(() => {
                                    const graded = gradeQuiz();
                                    const score = graded.filter(a => a.isCorrect).length;
                                    const allCorrect = score === graded.length;
                                    return (
                                        <>
                                            <div style={{
                                                padding: '10px 16px', borderRadius: 8, marginBottom: 12,
                                                background: allCorrect ? '#f0fdf4' : '#fef2f2',
                                                border: allCorrect ? '1px solid #bbf7d0' : '1px solid #fecaca',
                                                color: allCorrect ? '#166534' : '#991b1b',
                                                fontSize: 14, fontWeight: 600,
                                            }}>
                                                Score: {score} / {graded.length}
                                                {allCorrect ? ' — Passed! ✅' : ' — Not all correct ❌'}
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                {graded.map((item, idx) => (
                                                    <div key={idx} style={{
                                                        padding: '8px 12px', borderRadius: 6, fontSize: 13,
                                                        background: item.isCorrect ? '#f0fdf4' : '#fef2f2',
                                                        border: `1px solid ${item.isCorrect ? '#bbf7d0' : '#fecaca'}`,
                                                    }}>
                                                        <strong>{item.prompt}</strong>
                                                        <div style={{ marginTop: 2 }}>
                                                            Your answer: {item.userAnswer || '(none)'}
                                                            {item.isCorrect ? ' ✅' : ` ❌ (Correct: ${item.correctAnswer})`}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <p style={{ fontSize: 12, color: '#64748b', margin: '12px 0 0', fontStyle: 'italic' }}>
                                                Sign below to confirm your training content review and quiz submission.
                                            </p>
                                        </>
                                    );
                                })()}
                            </div>
                        )}
                    </div>
                )}

                {/* Signature step */}
                {(step === 'signing' || step === 'submitting') && (
                    <div style={{ padding: '20px' }}>
                        <p style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: '0 0 8px' }}>
                            {hasQuiz
                                ? 'Sign to confirm your training review and quiz submission'
                                : 'Sign to confirm you have reviewed this content'
                            }
                        </p>
                        <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px' }}>
                            Your signature confirms that you have viewed &ldquo;{topic.description}&rdquo;
                            {hasQuiz ? ' and submitted your quiz answers' : ''} and acknowledges completion.
                        </p>
                        {error && (
                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#dc2626' }}>
                                {error}
                            </div>
                        )}
                        <div style={{
                            position: 'relative',
                            border: hasSignature ? '2px solid #e2e8f0' : '2px dashed #cbd5e1',
                            borderRadius: 8,
                            background: '#fff',
                            cursor: 'crosshair',
                            touchAction: 'none',
                        }}>
                            <canvas
                                ref={canvasRef}
                                style={{ display: 'block', width: '100%', height: 120, borderRadius: 6 }}
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                onTouchStart={startDrawing}
                                onTouchMove={draw}
                                onTouchEnd={stopDrawing}
                            />
                            {!hasSignature && (
                                <span style={{
                                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                                    fontSize: 14, color: '#94a3b8', pointerEvents: 'none', userSelect: 'none',
                                }}>
                                    Sign here
                                </span>
                            )}
                        </div>
                        {hasSignature && (
                            <div style={{ textAlign: 'right', marginTop: 4 }}>
                                <button onClick={clearCanvas} style={{
                                    background: 'none', border: 'none', color: '#64748b', fontSize: 12, cursor: 'pointer',
                                }}>
                                    Clear signature
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Done step */}
                {step === 'done' && (
                    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                        <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
                        <p style={{ fontSize: 16, fontWeight: 600, color: '#166534', margin: '0 0 4px' }}>
                            Training Confirmed
                        </p>
                        <p style={{ fontSize: 13, color: '#15803d' }}>
                            Your completion has been recorded with your signature.
                            {hasQuiz && ' Quiz response saved.'}
                        </p>
                    </div>
                )}

                {/* Metadata */}
                <div className="tcv-metadata">
                    <span className="tcv-meta-tag">
                        <span className="tcv-style-dot" style={{ background: styleColor }} />
                        {topic.style}
                    </span>
                    <span className="tcv-meta-tag">ID: {topic.topicId}</span>
                    <span className="tcv-meta-tag">{topic.timeValue} min</span>
                </div>

                {/* Footer */}
                <div className="tcv-footer">
                    {step === 'viewing' && (
                        <>
                            <button className="tcv-btn-close" onClick={onClose}>Close</button>
                            {onMarkAsViewed && (
                                <button
                                    className={`tcv-btn-mark ${!contentCompleted ? 'tcv-btn-disabled' : ''}`}
                                    onClick={handleReviewClick}
                                    style={{ opacity: contentCompleted ? 1 : 0.6 }}
                                >
                                    {reviewButtonLabel}
                                </button>
                            )}
                        </>
                    )}
                    {step === 'quiz' && !quizShowReview && (
                        <>
                            <button className="tcv-btn-close" onClick={() => {
                                if (quizCurrentIndex > 0) { quizGoBack(); }
                                else { setStep('viewing'); setQuizQuestions([]); setQuizCurrentIndex(0); setQuizAnswers({}); }
                            }}>
                                {quizCurrentIndex > 0 ? 'Previous' : 'Back'}
                            </button>
                            <button
                                className="tcv-btn-mark"
                                onClick={quizGoNext}
                                disabled={!quizAnswers[quizCurrentIndex]}
                                style={{ opacity: quizAnswers[quizCurrentIndex] ? 1 : 0.5 }}
                            >
                                {quizCurrentIndex === quizQuestions.length - 1 ? 'Review Answers' : 'Next'}
                            </button>
                        </>
                    )}
                    {step === 'quiz' && quizShowReview && (
                        <>
                            <BackButton
                                onClick={() => setQuizShowReview(false)}
                                buttonText="Back to Questions"
                                buttonClasses="back-button--inline"
                            />
                            <button className="tcv-btn-mark" onClick={handleQuizContinueToSign}>
                                Continue to Sign
                            </button>
                        </>
                    )}
                    {step === 'signing' && (
                        <>
                            <button className="tcv-btn-close" onClick={() => {
                                clearCanvas();
                                setStep(hasQuiz && quizQuestions.length > 0 ? 'quiz' : 'viewing');
                                if (hasQuiz) setQuizShowReview(true);
                            }}>
                                Back
                            </button>
                            <button
                                className="tcv-btn-mark"
                                onClick={handleSubmitWithSignature}
                                disabled={!hasSignature}
                                style={{ opacity: hasSignature ? 1 : 0.5 }}
                            >
                                Submit & Record Completion
                            </button>
                        </>
                    )}
                    {step === 'submitting' && (
                        <button className="tcv-btn-mark" disabled style={{ opacity: 0.6 }}>
                            Submitting...
                        </button>
                    )}
                    {step === 'done' && (
                        <button className="tcv-btn-close" onClick={onClose} style={{ flex: 1 }}>
                            Close
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TopicContentViewerModal;
